import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function StepUpDialog({ open, onOpenChange, actions, onAuthorized }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setEmail(""); setPassword(""); }
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pos/step-up", { email, password, actions });
      toast.success(t("pos.authorized_by", { email: data.manager_email }));
      onAuthorized(data.token, data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pos-stepup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5" />{t("pos.stepup_title")}</DialogTitle>
          <DialogDescription>{t("pos.stepup_body")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("pos.manager_email")}</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} autoFocus data-testid="pos-stepup-email" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("pos.manager_password")}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && email && password) submit(); }} data-testid="pos-stepup-password" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pos-stepup-cancel">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy || !email || !password} data-testid="pos-stepup-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t("pos.authorize")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
